import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Button, EditArticleButton } from '@/components/atoms';
import { formatarNumero } from '@/i18n/formato';
import { destinationsApi } from '@/services/config';
import {
  optimizationApi,
  type ContentSignal,
  type SignalStatus,
  type SuggestResult,
} from '@/services/optimization';

import styles from './Otimizacao.module.css';

const COLS: { status: SignalStatus; titulo: string }[] = [
  { status: 'open', titulo: 'Detectado' },
  { status: 'in_progress', titulo: 'Em correção' },
  { status: 'resolved', titulo: 'Corrigido' },
];

const TIPO_LABEL: Record<string, string> = {
  striking_distance: 'Striking distance',
  ctr_gap: 'CTR baixo',
  quality_bounce: 'Bounce de qualidade',
  content_gap: 'Content gap',
  decay: 'Queda',
  cwv: 'Core Web Vitals',
};

const sevTone = (s: string) => (s === 'high' ? 'crit' : s === 'medium' ? 'warn' : 'neutral');
const pct = (v: unknown) => (typeof v === 'number' ? `${(v * 100).toFixed(1)}%` : '—');
const num = (v: unknown) => (typeof v === 'number' ? formatarNumero(Math.round(v)) : '—');

export function Otimizacao({
  site: propSite,
  embedded = false,
}: { site?: string; embedded?: boolean } = {}) {
  const { t } = useTranslation(['analytics', 'common']);
  const qc = useQueryClient();
  const [siteState, setSite] = useState(''); // B-34 F0: sem default global
  const site = propSite ?? siteState;
  const [suggestFor, setSuggestFor] = useState<ContentSignal | null>(null);

  const sitesQ = useQuery({
    queryKey: ['wp-sites', 'active'],
    queryFn: () => destinationsApi.list(),
    staleTime: 300_000,
  });
  useEffect(() => {
    if (!siteState && sitesQ.data?.length) setSite(sitesQ.data[0].slug);
  }, [siteState, sitesQ.data]);

  const openQ = useQuery({
    queryKey: ['signals', site, 'open'],
    queryFn: () => optimizationApi.list({ site, status: 'open', limit: 100 }),
  });
  const progQ = useQuery({
    queryKey: ['signals', site, 'in_progress'],
    queryFn: () => optimizationApi.list({ site, status: 'in_progress', limit: 100 }),
  });
  const doneQ = useQuery({
    queryKey: ['signals', site, 'resolved'],
    queryFn: () => optimizationApi.list({ site, status: 'resolved', limit: 100 }),
  });
  const byStatus: Record<SignalStatus, typeof openQ> = {
    open: openQ,
    in_progress: progQ,
    resolved: doneQ,
    dismissed: openQ,
  };

  const recompute = useMutation({
    mutationFn: () => optimizationApi.recompute(site),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['signals'] }),
  });
  const cwv = useMutation({
    mutationFn: () => optimizationApi.cwvSync(site),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['signals'] }),
  });
  const move = useMutation({
    mutationFn: (v: { id: string; status: SignalStatus }) =>
      optimizationApi.setStatus(v.id, v.status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['signals'] }),
  });

  return (
    <section className={styles.root}>
      {embedded ? (
        <div className={styles.controls} style={{ marginBottom: 'var(--sp-3)' }}>
          <span className={styles.subtitle} style={{ flex: 1 }}>
            Sinais de SEO priorizados pelos dados (cliques em jogo). Detecta → corrige → mede.
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => recompute.mutate()}
            loading={recompute.isPending}
          >
            Recalcular
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => cwv.mutate()}
            loading={cwv.isPending}
            title="Mede Core Web Vitals (LCP/CLS/INP) das matérias com mais tráfego — pode levar 1-2 min"
          >
            Medir CWV
          </Button>
        </div>
      ) : (
        <header className={styles.head}>
          <div>
            <h1 className={styles.title}>{t('analytics:optimization.title')}</h1>
            <p className={styles.subtitle}>
              Sinais de SEO priorizados pelos dados (cliques em jogo). Detecta → corrige → mede de
              novo.
            </p>
          </div>
          <div className={styles.controls}>
            <select
              className={styles.select}
              value={site}
              onChange={(e) => setSite(e.target.value)}
            >
              {(sitesQ.data ?? []).map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => recompute.mutate()}
              loading={recompute.isPending}
            >
              Recalcular
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => cwv.mutate()}
              loading={cwv.isPending}
              title="Mede Core Web Vitals (LCP/CLS/INP) das matérias com mais tráfego — pode levar 1-2 min"
            >
              Medir CWV
            </Button>
          </div>
        </header>
      )}

      <div className={styles.board}>
        {COLS.map((c) => {
          const colQ = byStatus[c.status];
          const list = colQ.data ?? [];
          return (
            <div key={c.status} className={styles.lane}>
              <div className={styles.laneHead}>
                <span>{c.titulo}</span>
                <span className={styles.laneCount}>{list.length}</span>
              </div>
              <div className={styles.laneBody}>
                {list.map((sig) => (
                  <SignalCard
                    key={sig.id}
                    sig={sig}
                    onSuggest={() => setSuggestFor(sig)}
                    onMove={(status) => move.mutate({ id: sig.id, status })}
                  />
                ))}
                {list.length === 0 && !colQ.isLoading && <p className={styles.empty}>—</p>}
              </div>
            </div>
          );
        })}
      </div>

      {suggestFor && (
        <SuggestModal
          sig={suggestFor}
          onClose={() => setSuggestFor(null)}
          onStart={() => {
            move.mutate({ id: suggestFor.id, status: 'in_progress' });
            setSuggestFor(null);
          }}
        />
      )}
    </section>
  );
}

function SignalCard({
  sig,
  onSuggest,
  onMove,
}: {
  sig: ContentSignal;
  onSuggest: () => void;
  onMove: (s: SignalStatus) => void;
}) {
  const p = sig.payload;
  const assistivel = sig.signal_type === 'striking_distance' || sig.signal_type === 'ctr_gap';
  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <Badge tone={sevTone(sig.severity)} size="xs">
          {TIPO_LABEL[sig.signal_type] ?? sig.signal_type}
        </Badge>
        <span className={styles.prio} title="Prioridade ≈ cliques em jogo">
          ≈ {Math.round(sig.score)} cliques
        </span>
      </div>
      {sig.ref_key && <p className={styles.query}>“{sig.ref_key}”</p>}
      <div className={styles.metrics}>
        {'position' in p && (
          <span>
            pos <b>{typeof p.position === 'number' ? p.position.toFixed(1) : '—'}</b>
          </span>
        )}
        {'impressions' in p && <span>{num(p.impressions)} impr</span>}
        {'ctr' in p && <span>CTR {pct(p.ctr)}</span>}
        {'pageviews' in p && <span>{num(p.pageviews)} pv</span>}
        {'engagement_rate' in p && <span>engaj {pct(p.engagement_rate)}</span>}
        {typeof p.cls === 'number' && (
          <span>
            CLS <b>{p.cls}</b>
          </span>
        )}
        {typeof p.lcp_ms === 'number' && <span>LCP {(p.lcp_ms / 1000).toFixed(1)}s</span>}
        {typeof p.inp_ms === 'number' && <span>INP {p.inp_ms}ms</span>}
      </div>
      <div className={styles.actions}>
        <EditArticleButton id={sig.wp_post_id} site={sig.site} size="sm" variant="ghost" />
        {assistivel && sig.status !== 'resolved' && (
          <Button size="sm" variant="secondary" onClick={onSuggest}>
            Gerar variações
          </Button>
        )}
        {sig.status === 'open' && (
          <Button size="sm" variant="ghost" onClick={() => onMove('dismissed')} title="Dispensar">
            ✕
          </Button>
        )}
        {sig.status === 'in_progress' && (
          <Button size="sm" onClick={() => onMove('resolved')}>
            Corrigido
          </Button>
        )}
        {sig.status === 'resolved' && (
          <Button size="sm" variant="ghost" onClick={() => onMove('open')}>
            Reabrir
          </Button>
        )}
      </div>
    </div>
  );
}

function SuggestModal({
  sig,
  onClose,
  onStart,
}: {
  sig: ContentSignal;
  onClose: () => void;
  onStart: () => void;
}) {
  const qc = useQueryClient();
  const [copied, setCopied] = useState<string | null>(null);
  const q = useQuery<SuggestResult>({
    queryKey: ['suggest', sig.id],
    queryFn: () => optimizationApi.suggest(sig.id),
    staleTime: Infinity,
    retry: false,
  });
  const apply = useMutation({
    mutationFn: (v: { title: string; meta: string }) => optimizationApi.apply(sig.id, v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signals'] });
      onClose();
    },
  });

  const copy = (text: string, key: string) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    window.setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.modalHead}>
          <div>
            <h2 className={styles.modalTitle}>Otimizar para “{sig.ref_key}”</h2>
            <span className={styles.modalSub}>
              post #{sig.wp_post_id} · 3 variações de título + meta para ganhar o clique
            </span>
          </div>
          <button className={styles.x} onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        {q.isLoading && <p className={styles.muted}>Gerando variações…</p>}
        {q.isError && <p className={styles.err}>Falha ao gerar. Tente de novo.</p>}

        {q.data?.variations.map((v, i) => (
          <div key={i} className={styles.variation}>
            <div className={styles.varRow}>
              <span className={styles.varLabel}>
                Título <small>({v.title.length}c)</small>
              </span>
              <button className={styles.copy} onClick={() => copy(v.title, `t${i}`)}>
                {copied === `t${i}` ? 'copiado ✓' : 'copiar'}
              </button>
            </div>
            <p className={styles.varTitle}>{v.title}</p>
            <div className={styles.varRow}>
              <span className={styles.varLabel}>
                Meta <small>({v.meta.length}c)</small>
              </span>
              <button className={styles.copy} onClick={() => copy(v.meta, `m${i}`)}>
                {copied === `m${i}` ? 'copiado ✓' : 'copiar'}
              </button>
            </div>
            <p className={styles.varMeta}>{v.meta}</p>
            <div className={styles.varApply}>
              <Button
                size="sm"
                loading={apply.isPending && apply.variables?.title === v.title}
                onClick={() => {
                  if (window.confirm(`Aplicar no WP agora?\n\nTítulo: ${v.title}\nMeta: ${v.meta}`))
                    apply.mutate({ title: v.title, meta: v.meta });
                }}
              >
                Aplicar no WP
              </Button>
            </div>
          </div>
        ))}

        {apply.isError && <p className={styles.err}>Falha ao aplicar no WP. Tente de novo.</p>}

        <div className={styles.modalActions}>
          <EditArticleButton id={sig.wp_post_id} site={sig.site} label="Abrir editor" />
          <Button variant="secondary" onClick={onStart}>
            Só marcar “em correção”
          </Button>
        </div>
        <p className={styles.note}>
          “Aplicar no WP” troca o título e a meta description da matéria publicada na hora (+ alinha
          a focus keyword à query). O sinal vai para “Em correção” e some sozinho quando a posição
          melhorar.
        </p>
      </div>
    </div>
  );
}
